import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Button, Heading, Text } from '../ui';
import { BookCoverPlaceholder } from './BookCoverPlaceholder';
import './BookCard.css';

interface BookCardProps {
  book: {
    id: string;
    slug: string | null;
    title: string;
    description: string | null;
    cover_image_url: string | null;
    publication_year: number | null;
    book_authors: Array<{
      author_order: number;
      authors: {
        id: string;
        name: string;
        slug: string | null;
        bio: string | null;
      };
    }>;
    book_category_relations?: Array<{
      book_categories: {
        id: string;
        name: string;
        slug: string;
        color: string | null;
      };
    }>;
    book_tag_relations?: Array<{
      book_tags: {
        id: string;
        name: string;
        slug: string;
        color: string | null;
      };
    }>;
  };
  isAdmin?: boolean;
  animationDelay?: number;
}

export const BookCard: React.FC<BookCardProps> = ({ book, isAdmin, animationDelay = 0 }) => {
  const navigate = useNavigate();

  // Sortuj autorów po author_order i pobierz ich imiona
  const authors = book.book_authors
    ?.filter(ba => ba.authors) // Filtruj null/undefined
    .sort((a, b) => a.author_order - b.author_order)
    .map(ba => ba.authors.name)
    .join(', ') || '';

  // Pobierz kategorie i tagi z relacji
  const categories =
    book.book_category_relations
      ?.map(rel => rel.book_categories)
      .filter((category): category is NonNullable<typeof category> => Boolean(category)) || [];
  const primaryCategory = categories[0] || null;

  return (
    <Card
      variant="glass"
      className="book-card fade-in-up"
      style={{ animationDelay: `${animationDelay}ms`, cursor: 'pointer' }}
      onClick={() => navigate(`/biblioteka/${book.slug || book.id}`)}
    >
      <Box className="book-card-cover">
        {book.cover_image_url ? (
          <img src={book.cover_image_url} alt={book.title} />
        ) : (
          <BookCoverPlaceholder title={book.title} size="medium" />
        )}
      </Box>

      <CardContent className="book-card-content">
        <Heading as="h2" size="2" weight="semibold" className="book-card-title">
          {book.title}
        </Heading>
        
        {authors && (
          <Text size="2" color="accent" weight="semibold" className="book-card-author">
            {authors}
          </Text>
        )}

        {(primaryCategory || book.publication_year) && (
          <Box className="book-card-meta">
            {primaryCategory && (
              <Text size="2" color="muted" className="book-card-category">
                {primaryCategory.name}
              </Text>
            )}
            {primaryCategory && book.publication_year && (
              <span className="book-card-meta-sep" aria-hidden="true">•</span>
            )}
            {book.publication_year && (
              <Text size="1" color="muted" className="book-card-year">
                {book.publication_year}
              </Text>
            )}
          </Box>
        )}

        <Box className="book-card-actions">
          <Button 
            variant="solid"
            size="2"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/biblioteka/${book.slug || book.id}`);
            }}
          >
            Zobacz szczegóły
          </Button>
          {isAdmin && (
            <Button
              variant="soft"
              size="2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/library/edit/${book.id}`);
              }}
            >
              Edytuj
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

